import React, { useEffect, useMemo, useState } from "react";
import { createDealer, deleteDealer, fetchDealers, updateDealer } from "../api";

const emptyForm = { name: "", city: "", contact: "", address: "" };

export default function AdminDealerManager({ user }) {
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [formData, setFormData] = useState(emptyForm);

    const loadDealers = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchDealers();
            if (!Array.isArray(data)) {
                throw new Error("The dealer service returned an invalid response.");
            }
            setDealers(data);
        } catch (err) {
            setError(err.message || "Failed to load dealers.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDealers();
    }, []);

    const visibleDealers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return dealers
            .filter((dealer) =>
                [dealer.name, dealer.city, dealer.contact, dealer.address]
                    .some((value) => String(value || "").toLowerCase().includes(term))
            )
            .sort((a, b) =>
                String(a.city || "").localeCompare(String(b.city || "")) ||
                String(a.name || "").localeCompare(String(b.name || ""))
            );
    }, [dealers, searchTerm]);

    const resetForm = () => {
        setFormData(emptyForm);
        setEditingId(null);
    };

    const updateField = (field) => (event) => {
        setFormData((current) => ({ ...current, [field]: event.target.value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formData.name.trim() || !formData.city.trim()) {
            setError("Dealer name and city are required.");
            return;
        }

        setSubmitting(true);
        setError("");
        try {
            if (editingId !== null) {
                await updateDealer(user.token, editingId, formData);
            } else {
                await createDealer(user.token, formData);
            }
            resetForm();
            await loadDealers();
        } catch (err) {
            setError(err.message || "Failed to save dealer.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (dealer) => {
        setEditingId(dealer.id);
        setFormData({
            name: dealer.name || "",
            city: dealer.city || "",
            contact: dealer.contact || "",
            address: dealer.address || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (dealer) => {
        if (!window.confirm(`Delete ${dealer.name}?`)) return;

        setDeletingId(dealer.id);
        setError("");
        try {
            await deleteDealer(user.token, dealer.id);
            if (editingId === dealer.id) resetForm();
            await loadDealers();
        } catch (err) {
            setError(err.message || "Failed to delete dealer.");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <section className="adm-dealers">
            <style>{`
                .adm-dealers {
                    padding: 24px;
                    border: 1px solid #ded5c3;
                    border-radius: 16px;
                    background: #fffdf8;
                    color: #1d3329;
                }
                .adm-dealers * { box-sizing: border-box; }
                .adm-dealers h2 {
                    margin: 0 0 24px;
                    color: #173f2d;
                }
                .adm-dealer-grid {
                    display: grid;
                    grid-template-columns: minmax(260px, 1fr) minmax(0, 2.25fr);
                    gap: 28px;
                    align-items: start;
                }
                .adm-dealer-form {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                    padding: 20px;
                    border: 1px solid #ded5c3;
                    border-radius: 12px;
                    background: #f7f8f7;
                }
                .adm-dealer-form-header,
                .adm-dealer-list-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }
                .adm-dealer-form h3,
                .adm-dealer-list-header h3 {
                    margin: 0;
                    color: #1d275f;
                    font-size: 18px;
                }
                .adm-dealer-form label {
                    display: block;
                    margin-bottom: 6px;
                    color: #173f2d;
                    font-size: 14px;
                    font-weight: 600;
                }
                .adm-dealer-form input,
                .adm-dealer-form textarea,
                .adm-dealer-search {
                    width: 100%;
                    padding: 11px 13px;
                    border: 1px solid #d9cdb7;
                    border-radius: 9px;
                    background: #ffffff;
                    color: #263b31;
                    font: inherit;
                }
                .adm-dealer-form textarea { resize: vertical; }
                .adm-dealer-submit {
                    padding: 12px 16px;
                    border: 0;
                    border-radius: 999px;
                    background: #174a34;
                    color: #ffffff;
                    font-weight: 700;
                    cursor: pointer;
                }
                .adm-dealer-cancel {
                    border: 0;
                    background: transparent;
                    color: #174a34;
                    font-weight: 700;
                    cursor: pointer;
                }
                .adm-dealer-list-header {
                    margin-bottom: 16px;
                }
                .adm-dealer-search { max-width: 260px; }
                .adm-dealer-error {
                    margin-bottom: 14px;
                    padding: 10px 12px;
                    border-radius: 8px;
                    background: #fff0f1;
                    color: #a4212d;
                }
                .adm-dealer-table-wrap {
                    max-height: 600px;
                    overflow: auto;
                    border: 1px solid #ded5c3;
                    border-radius: 12px;
                    background: #ffffff;
                }
                .adm-dealer-table {
                    display: table !important;
                    width: 100% !important;
                    min-width: 760px;
                    border-collapse: separate !important;
                    border-spacing: 0 !important;
                    color: #263b31 !important;
                    background: #ffffff !important;
                    font-size: 13px !important;
                    line-height: 1.45 !important;
                }
                .adm-dealer-table thead { display: table-header-group !important; }
                .adm-dealer-table tbody { display: table-row-group !important; }
                .adm-dealer-table tr {
                    display: table-row !important;
                    height: auto !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                .adm-dealer-table th,
                .adm-dealer-table td {
                    display: table-cell !important;
                    height: auto !important;
                    padding: 12px 14px !important;
                    border: 0 !important;
                    border-bottom: 1px solid #ece6da !important;
                    text-align: left !important;
                    vertical-align: top !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    color: #263b31 !important;
                    background: #ffffff !important;
                    white-space: normal !important;
                }
                .adm-dealer-table th {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    color: #ffffff !important;
                    background: #1c4a35 !important;
                    font-weight: 700 !important;
                    white-space: nowrap !important;
                }
                .adm-dealer-table tr.is-editing td {
                    background: #edf8f1 !important;
                }
                .adm-dealer-name {
                    min-width: 170px;
                    color: #172d23 !important;
                    font-weight: 700 !important;
                }
                .adm-dealer-actions {
                    display: flex !important;
                    gap: 6px;
                    white-space: nowrap;
                }
                .adm-dealer-actions button {
                    display: inline-block !important;
                    padding: 6px 10px !important;
                    border: 0 !important;
                    border-radius: 6px !important;
                    color: #ffffff !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                    line-height: 1.2 !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    cursor: pointer;
                }
                .adm-dealer-edit { background: #aa7600 !important; }
                .adm-dealer-delete { background: #c93442 !important; }
                .adm-dealer-actions button:disabled {
                    opacity: .65 !important;
                    cursor: wait;
                }
                .adm-dealer-empty {
                    padding: 30px;
                    border: 1px solid #ded5c3;
                    border-radius: 12px;
                    color: #647067;
                    text-align: center;
                }
                @media (max-width: 850px) {
                    .adm-dealer-grid { grid-template-columns: 1fr; }
                }
                @media (max-width: 560px) {
                    .adm-dealers { padding: 16px; }
                    .adm-dealer-list-header {
                        align-items: stretch;
                        flex-direction: column;
                    }
                    .adm-dealer-search { max-width: none; }
                }
            `}</style>

            <h2>Manage Dealers</h2>

            <div className="adm-dealer-grid">
                <form className="adm-dealer-form" onSubmit={handleSubmit}>
                    <div className="adm-dealer-form-header">
                        <h3>{editingId !== null ? "Edit Dealer" : "Add New Dealer"}</h3>
                        {editingId !== null && (
                            <button type="button" className="adm-dealer-cancel" onClick={resetForm}>
                                Cancel
                            </button>
                        )}
                    </div>

                    <div>
                        <label htmlFor="dealer-name">Dealer Name *</label>
                        <input id="dealer-name" value={formData.name} onChange={updateField("name")} required />
                    </div>
                    <div>
                        <label htmlFor="dealer-city">City *</label>
                        <input id="dealer-city" value={formData.city} onChange={updateField("city")} required />
                    </div>
                    <div>
                        <label htmlFor="dealer-contact">Contact</label>
                        <input id="dealer-contact" value={formData.contact} onChange={updateField("contact")} />
                    </div>
                    <div>
                        <label htmlFor="dealer-address">Address</label>
                        <textarea id="dealer-address" rows="4" value={formData.address} onChange={updateField("address")} />
                    </div>
                    <button type="submit" className="adm-dealer-submit" disabled={submitting}>
                        {submitting
                            ? (editingId !== null ? "Saving..." : "Adding...")
                            : (editingId !== null ? "Save Changes" : "+ Add Dealer")}
                    </button>
                </form>

                <div>
                    <div className="adm-dealer-list-header">
                        <h3>Current Dealers ({dealers.length})</h3>
                        <input
                            className="adm-dealer-search"
                            type="search"
                            placeholder="Search dealers..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </div>

                    {error && <div className="adm-dealer-error">{error}</div>}

                    {loading ? (
                        <div className="adm-dealer-empty">Loading dealers...</div>
                    ) : visibleDealers.length === 0 ? (
                        <div className="adm-dealer-empty">
                            {searchTerm ? "No dealers match your search." : "No dealers added yet."}
                        </div>
                    ) : (
                        <div className="adm-dealer-table-wrap">
                            <table className="adm-dealer-table">
                                <thead>
                                    <tr>
                                        <th>Dealer</th>
                                        <th>City</th>
                                        <th>Contact</th>
                                        <th>Address</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleDealers.map((dealer) => (
                                        <tr key={dealer.id} className={editingId === dealer.id ? "is-editing" : ""}>
                                            <td className="adm-dealer-name">{dealer.name || "—"}</td>
                                            <td>{dealer.city || "—"}</td>
                                            <td>{dealer.contact || "—"}</td>
                                            <td>{dealer.address || "—"}</td>
                                            <td>
                                                <div className="adm-dealer-actions">
                                                    <button
                                                        type="button"
                                                        className="adm-dealer-edit"
                                                        onClick={() => handleEdit(dealer)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="adm-dealer-delete"
                                                        disabled={deletingId === dealer.id}
                                                        onClick={() => handleDelete(dealer)}
                                                    >
                                                        {deletingId === dealer.id ? "Deleting..." : "Delete"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
